import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import useCreateSpecialNumber from './queries/useCreateSpecialNumber';
import useGetSpecialNumber from './queries/useGetSpecialNumber';
import useUpdateSpecialNumber from './queries/useUpdateSpecialNumber';

import FormBase from '@/components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import {
    breadCrumbTrail,
    breadCrumbTrailEdit,
    addHeader,
    editHeader,
    initialState,
    specialNumbersPath,
} from './constants';
import { specialNumberSchema } from './schema';
import SpecialNumberNameField from './formFields/SpecialNumberNameField';
import PatternField from './formFields/PatternField';
import ActiveField from './formFields/ActiveField';

const SpecialNumberForm = ({ mode = 'add' }) => {
    const isEdit = mode === 'edit';
    const navigate = useNavigate();
    const { id } = useParams();
    const { darkMode } = useSelector((state) => state.settings);
    const [error, setError] = useState(null);

    const {
        data: specialNumberData,
        isLoading: specialNumberLoading,
        isFetching: specialNumberFetching,
        isError: notFound,
    } = useGetSpecialNumber(id, isEdit);

    const handleSuccess = () => {
        toast.success(
            isEdit
                ? 'Special Number updated successfully!'
                : 'Special Number created successfully!',
        );
        navigate(specialNumbersPath);
    };

    const handleError = (err) => {
        console.error(
            `Failed to ${isEdit ? 'update' : 'create'} special number:`,
            err,
        );
        setError(
            err?.response?.data?.message ||
                `Failed to ${isEdit ? 'update' : 'create'} special number. Please try again.`,
        );
    };

    const { mutate: createSpecialNumber, isPending: createPending } =
        useCreateSpecialNumber(handleSuccess, handleError);

    const { mutate: updateSpecialNumber, isPending: updatePending } =
        useUpdateSpecialNumber(handleSuccess, handleError);

    const isPending = isEdit ? updatePending : createPending;

    const finalFormSubmit = async (values, { setErrors, setTouched }) => {
        setError(null);

        let validated;
        try {
            validated = await specialNumberSchema.validate(values, {
                abortEarly: false,
            });
        } catch (validationError) {
            const fieldErrors = {};
            (validationError.inner || []).forEach((innerError) => {
                if (innerError.path && !fieldErrors[innerError.path]) {
                    fieldErrors[innerError.path] = innerError.message;
                }
            });
            if (Object.keys(fieldErrors).length > 0) {
                setErrors(fieldErrors);
                setTouched(
                    Object.keys(fieldErrors).reduce(
                        (touchedFields, field) => ({
                            ...touchedFields,
                            [field]: true,
                        }),
                        {},
                    ),
                );
            } else {
                setError(validationError.message);
            }
            return;
        }

        if (isEdit) {
            updateSpecialNumber({
                ...specialNumberData,
                id,
                name: validated.name,
                pattern: validated.pattern,
                isActive: !!validated.isActive,
            });
        } else {
            createSpecialNumber({
                name: validated.name,
                pattern: validated.pattern,
                isActive: !!validated.isActive,
            });
        }
    };

    const formFields = (errors, touched, values, setFieldValue) =>
        [
            <SpecialNumberNameField
                key="name"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <PatternField
                key="pattern"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <ActiveField
                key="isActive"
                darkMode={darkMode}
                values={values}
                setFieldValue={setFieldValue}
            />,
            error && (
                <Alert key="error" status="error" sx={{ margin: '25px 0' }}>
                    <AlertIcon />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            ),
        ].filter(Boolean);

    if (isEdit && (specialNumberLoading || specialNumberFetching)) {
        return <LoadingIndicator />;
    }

    if (isEdit && notFound) {
        return (
            <FormBase
                breadCrumbTrail={breadCrumbTrailEdit}
                header={editHeader}
                initialValues={{ name: '', pattern: '', isActive: true }}
                finalFormSubmit={async () => {}}
                hideSubmitButton
                backLink={[specialNumbersPath, { state: { index: 0 } }]}>
                {() => [
                    <p key="not-found">
                        This special number no longer exists. It may have
                        already been deleted.
                    </p>,
                ]}
            </FormBase>
        );
    }

    const formInitialValues = isEdit
        ? {
              name: specialNumberData?.name || '',
              pattern: specialNumberData?.pattern || '',
              isActive: specialNumberData?.isActive ?? true,
          }
        : initialState;

    return (
        <FormBase
            isSubmitDisabled={isPending}
            isLoading={isPending}
            breadCrumbTrail={isEdit ? breadCrumbTrailEdit : breadCrumbTrail}
            header={isEdit ? editHeader : addHeader}
            initialValues={formInitialValues}
            finalFormSubmit={finalFormSubmit}
            backLink={[specialNumbersPath, { state: { index: 0 } }]}>
            {({ errors, touched, values, setFieldValue }) =>
                formFields(errors, touched, values, setFieldValue)
            }
        </FormBase>
    );
};

export default SpecialNumberForm;
